import React, { useEffect, useRef, useState } from "react";
import { readAppearancePreference, saveAppearance } from "@/lib/appearance";
import DocumentAIConnection from "@/components/documents/DocumentAIConnection";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { canProgramAudit } from "./ProgramAudit";
import { Navigate, Link } from "react-router-dom";
import { Sun, Moon, Monitor, Link2 } from "lucide-react";
import "./background-settings.css";
import "./preferences-bento.css";

export default function BackgroundSettings() { return <Navigate to="/profile?view=preferences" replace />; }

export function ProfilePreferences() {
  const { user } = useAuth();
  const [connection, setConnection] = useState(null);
  const [connectionError, setConnectionError] = useState(false);
  const [provider, setProvider] = useState("none");
  const [managing, setManaging] = useState(false);
  const dialog = useRef(null);
  const manageButton = useRef(null);
  const [appearance, setAppearance] = useState(readAppearancePreference);
  useEffect(() => {
    let active = true;
    api.get("/documents/ai/connection").then(({ data }) => {
      if (active) { setConnection(data); setProvider(data.provider || "none"); }
    }).catch(() => { if (active) setConnectionError(true); });
    return () => { active = false; };
  }, []);
  useEffect(() => {
    const update = () => setAppearance(readAppearancePreference());
    window.addEventListener("storage", update);
    return () => window.removeEventListener("storage", update);
  }, []);
  useEffect(() => {
    if (managing && dialog.current && !dialog.current.open) dialog.current.showModal();
  }, [managing]);
  const closeManagement = () => {
    dialog.current?.close();
    setManaging(false);
    manageButton.current?.focus();
  };
  const connectionUpdated = data => {
    setConnection(data);
    setProvider(data.provider || "none");
  };
  const auditAvailable = canProgramAudit(user);
  const status = connection?.connected
    ? (connection.available ? "OpenAI connected" : "Needs attention")
    : "Not connected";

  return <div className={`toto-profile__preferences preferences-bento ${auditAvailable ? "has-program-audit" : ""}`} data-testid="profile-preferences">
    <section className="preferences-card preferences-appearance" aria-labelledby="appearance-title">
      <div className="preferences-card-heading"><Sun size={20} aria-hidden="true" /><h2 id="appearance-title">Appearance</h2></div>
      <p>Your workspace, on this device.</p>
      <fieldset className="preferences-appearance-choices"><legend className="sr-only">Workspace appearance</legend>
        {[{ id: "light", name: "Light", detail: "Soft porcelain", Icon: Sun },
          { id: "dark", name: "Dark", detail: "Neutral charcoal", Icon: Moon },
          { id: "system", name: "Use device setting", detail: "Follow appearance", Icon: Monitor }].map(({ id, name, detail, Icon }) => <label className="preferences-appearance-choice" key={id}>
          <input type="radio" name="appearance" value={id} checked={appearance === id} onChange={() => { saveAppearance(id); setAppearance(id); }} />
          <Icon size={28} aria-hidden="true" /><strong>{name}</strong><small>{detail}</small>
        </label>)}
      </fieldset>
    </section>

    <section className="preferences-card preferences-connections" aria-labelledby="connections-title">
      <div className="preferences-card-heading"><Link2 size={20} aria-hidden="true" /><h2 id="connections-title">Connections</h2></div>
      <div className="preferences-connection-section preferences-zenoti">
        <div className="preferences-connection-title"><h3>Zenoti</h3><span className="preferences-proposed">Proposed</span></div>
        <p>Sign in directly with your Zenoti account.</p>
        <div className="preferences-connection-row"><span>Manager-owned secure browser</span><button type="button" disabled aria-describedby="zenoti-proposed">Connect Zenoti</button></div>
        <span id="zenoti-proposed" className="sr-only">Proposed integration. Manager-owned Zenoti connections are not available yet.</span>
      </div>
      <div id="ai-assistance" className="preferences-connection-section preferences-ai">
        <div className="preferences-connection-title"><h3>AI</h3>{connection && <span className="preferences-connection-status" role="status">{status}</span>}</div>
        <p>OpenAI API today · Claude and Gemini proposed.</p>
        {connection ? <>
          <div className="preferences-ai-controls"><label>Your provider<select aria-label="Your AI provider" value={provider} onChange={event => setProvider(event.target.value)}>
            <option value="openai">OpenAI API</option><option value="none">No AI</option>
            <option value="anthropic" disabled>Claude · proposed</option><option value="gemini" disabled>Gemini · proposed</option>
          </select></label><button ref={manageButton} type="button" onClick={() => setManaging(true)}>Manage connection</button></div>
          <div className="preferences-ai-context"><span>Receipts and document drafts · separate API billing</span><button type="button" onClick={() => setManaging(true)}>Details</button></div>
        </> : <p role="status">{connectionError ? "Could not load your connection. Reload Preferences to try again." : "Loading connection…"}</p>}
      </div>
    </section>

    <section className="preferences-card preferences-accessibility" aria-labelledby="preferences-accessibility-title">
      <h2 id="preferences-accessibility-title">Accessibility</h2><p>Follows your device.</p>
      <ul><li><Monitor size={17} aria-hidden="true" />Reduced motion</li><li><Monitor size={17} aria-hidden="true" />Contrast preference</li></ul>
    </section>
    {auditAvailable && <section className="preferences-card preferences-audit" aria-labelledby="preferences-audit-title">
      <h2 id="preferences-audit-title">Program Audit</h2><p>Inspect views across your organization.</p><Link to="/settings/program-audit">Open Program Audit ↗</Link>
    </section>}

    {managing && connection && <dialog ref={dialog} className="preferences-connection-dialog" aria-labelledby="manage-ai-title" onCancel={event => { event.preventDefault(); closeManagement(); }} onClose={() => { setManaging(false); manageButton.current?.focus(); }}>
      <header><h2 id="manage-ai-title">Manage AI connection</h2><button type="button" aria-label="Close connection management" onClick={closeManagement}>Close</button></header>
      <DocumentAIConnection status={connection} initialProvider={provider} onChange={connectionUpdated} expanded />
    </dialog>}
  </div>;
}
